import React from "react";
import { format, isPast, isToday, isFuture } from "date-fns";
import StatusBadge from "../../../../components/JewelleryPlans/common/StatusBadge";
import { INSTALLMENT_STATUS } from "../../../../constants/jewellery.constant";

const InstallmentSchedule = ({ plan, schedule }) => {
  const getStatusColor = (status, dueDate) => {
    if (status === INSTALLMENT_STATUS.PAID) return "text-success";
    if (status === INSTALLMENT_STATUS.MISSED) return "text-danger";
    if (status === INSTALLMENT_STATUS.PENDING && isPast(new Date(dueDate)))
      return "text-warning";
    if (status === INSTALLMENT_STATUS.POSTPONED) return "text-blue-600";
    return "text-neutral-dark";
  };

  const getDueDateLabel = (dueDate, status) => {
    const date = new Date(dueDate);
    if (status === INSTALLMENT_STATUS.PAID) return "Paid";
    if (isToday(date)) return "Due Today";
    if (isPast(date)) return "Overdue";
    if (isFuture(date)) return format(date, "dd MMM yyyy");
    return format(date, "dd MMM yyyy");
  };

  // ✅ Use actual schedule data, not generated fallback
  const installments = schedule?.length > 0 ? schedule : [];

  // If no schedule data, show a message
  if (installments.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800">
          No installment schedule found. Please reload the plan details.
        </p>
      </div>
    );
  }

  // Calculate summary stats
  const paidCount = installments.filter(
    (i) => i.status === INSTALLMENT_STATUS.PAID
  ).length;
  const pendingCount = installments.filter(
    (i) => i.status === INSTALLMENT_STATUS.PENDING
  ).length;
  const missedCount = installments.filter(
    (i) => i.status === INSTALLMENT_STATUS.MISSED
  ).length;
  const postponedCount = installments.filter(
    (i) => i.status === INSTALLMENT_STATUS.POSTPONED || i.pauseAdjustment
  ).length;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-neutral-light rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <p className="text-neutral-dark">Total Installments</p>
            <p className="font-medium">{installments.length}</p>
          </div>
          <div>
            <p className="text-neutral-dark">Paid</p>
            <p className="font-medium text-success">{paidCount}</p>
          </div>
          <div>
            <p className="text-neutral-dark">Pending</p>
            <p className="font-medium text-warning">{pendingCount}</p>
          </div>
          <div>
            <p className="text-neutral-dark">Missed</p>
            <p className="font-medium text-danger">{missedCount}</p>
          </div>
          <div>
            <p className="text-neutral-dark">Adjusted (Pause)</p>
            <p className="font-medium text-blue-600">{postponedCount}</p>
          </div>
        </div>
      </div>

      {/* Pause Notice */}
      {plan?.pauseMetrics?.currentlyPaused && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            ⚠️ Plan is currently paused. Installment dates have been adjusted.
          </p>
        </div>
      )}

      {/* Schedule Table */}
      <div className="bg-background border border-neutral-light rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-light">
            <thead className="bg-neutral-light">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Original Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Amount Due
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Paid Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Grace Period Ends
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-neutral-light">
              {installments.map((installment) => {
                const isOverdue =
                  installment.status === INSTALLMENT_STATUS.PENDING &&
                  isPast(new Date(installment.dueDate));

                const wasAdjusted =
                  installment.pauseAdjustment ||
                  installment.originalDueDate ||
                  installment.notes?.includes("pause");

                return (
                  <tr
                    key={installment._id}
                    className={`
                      ${isOverdue ? "bg-danger-light" : ""}
                      ${wasAdjusted ? "bg-blue-50" : ""}
                      hover:bg-neutral-light transition-colors
                    `}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-text">
                      {installment.sequenceNumber}
                    </td>
                    <td
                      className={`px-4 py-4 whitespace-nowrap text-sm ${getStatusColor(
                        installment.status,
                        installment.dueDate
                      )}`}
                    >
                      <div>
                        <p className="font-medium">
                          {format(new Date(installment.dueDate), "dd MMM yyyy")}
                        </p>
                        {isOverdue && (
                          <p className="text-xs text-danger font-semibold">
                            Overdue
                          </p>
                        )}
                        {isToday(new Date(installment.dueDate)) && (
                          <p className="text-xs text-warning font-semibold">
                            Due Today
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-dark">
                      {installment.originalDueDate ||
                      installment.pauseAdjustment?.originalDate ? (
                        <div>
                          <p className="line-through text-gray-400">
                            {format(
                              new Date(
                                installment.originalDueDate ||
                                  installment.pauseAdjustment.originalDate
                              ),
                              "dd MMM yyyy"
                            )}
                          </p>
                          <p className="text-xs text-blue-600">Adjusted</p>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-text">
                      ₹
                      {(
                        installment.amountDue || plan.monthlyAmount
                      )?.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-text">
                      ₹{(installment.paidAmount || 0)?.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge
                        status={installment.status}
                        type="installment"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-dark">
                      {installment.gracePeriodEnds
                        ? format(
                            new Date(installment.gracePeriodEnds),
                            "dd MMM yyyy"
                          )
                        : "-"}
                    </td>
                    <td className="px-4 py-4 text-xs text-neutral-dark max-w-xs truncate">
                      {installment.notes || "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grace Period Note */}
      <div className="bg-secondary-light border border-secondary rounded-lg p-4">
        <p className="text-sm text-secondary-dark">
          ℹ️ Grace period of {plan.templateSnapshot?.graceDays || 7} days is
          applicable for each installment. Dates highlighted in blue have been
          adjusted due to plan pause.
        </p>
      </div>
    </div>
  );
};

export default InstallmentSchedule;
